// Standalone, chrome-free share target for a first-class app.
//
// Reached via /p/:projectSlug/b/:branch/share/apps/:viewName — a slug-addressed
// URL (single source of truth) that renders ONLY the app, full-screen, with NO
// cortex sidebar/nav. Access goes through the apps registry: GET
// /api/apps/resolve/:viewName returns the app + paired Flow project id iff the
// caller may open it (owner, explicitly shared, or org-visible) — a private app
// that wasn't shared renders an access-denied state instead of the embed. Only
// the app surface is ever exposed; the recipe/sources/flow graph behind it are
// not reachable from here. A loader covers the iframe until the view's React
// root mounts — identical gate to Apps.jsx.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Loader2, Lock, AlertTriangle, Plus, X, MessageSquare, Workflow,
  GraduationCap, GitBranch, ShieldCheck, BookText,
} from "lucide-react";
import { resolveApp, login } from "../services/apps";
// The share page is chrome-free (no Layout), so it mounts its own chat: the
// DATA ANALYST harness — read-only questions and charts over this project's
// governed data.
import AssistantChat from "../components/AssistantChat";
// Viewer companions beyond the chat, gathered into ONE speed dial (same idiom,
// and now the same LIST, as the in-cortex app view and the deployed portal).
//
// WHICH companions appear is decided ONCE, SERVER-SIDE: the resolve returns
// `companions` (shared/viewer-companions.js — the owner's Viewer options,
// nothing else). This page renders that list verbatim. It no longer intersects
// it with a data probe, and no longer narrows it by viewer_scope — that
// narrowing is exactly why a share recipient signed in as an app_user used to
// see one chat bubble where the same app in-cortex showed four buttons.
import { companionsFrom, companionOn } from "../services/companions";
import { loadProjectGuides } from "../components/tutorial/guides";
import TutorialFlow from "../components/tutorial/TutorialFlow";
import { dictionaryChildren } from "./Layout";

function embedUrl(flowProjectId, name, deepLink) {
  if (!flowProjectId || !name) return null;
  const base = `/flow-embed/projects/${flowProjectId}/endpoints/${encodeURIComponent(name)}/flow?autorunView=1`;
  const hash = (deepLink || "").replace(/^[#?]/, "");
  return hash ? `${base}#${hash}` : base;
}

export default function SharedApp() {
  const { viewName, projectSlug, branch } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const deepLink = (location.search || "").replace(/^\?/, "");
  const [flowProjectId, setFlowProjectId] = useState(null);
  // The server's effective companion list, ordered. `null` until the resolve
  // answers (and for a backend too old to send the field) — the dial mounts
  // only once it is an array, so no button ever flickers in and out.
  const [companions, setCompanions] = useState(null);
  const [guideSlugs, setGuideSlugs] = useState(null);
  const [err, setErr] = useState(null);
  const [denied, setDenied] = useState(false);
  // The app's OWN sign-in gate — shown when there is no session at all, so a
  // share link never redirects into cortex's login.
  const [needsAuth, setNeedsAuth] = useState(false);
  const [gateEmail, setGateEmail] = useState("");
  const [gatePassword, setGatePassword] = useState("");
  const [gateErr, setGateErr] = useState(null);
  const [gateBusy, setGateBusy] = useState(false);
  const [authNonce, setAuthNonce] = useState(0); // bumping it re-runs resolve
  const [viewReady, setViewReady] = useState(false);
  const iframeRef = useRef(null);
  // Speed-dial state. WHICH companions exist is the server's `companions`
  // (above) — these are only the dial's own interaction state and the guide
  // CONTENT a Guides press needs.
  const [dialOpen, setDialOpen] = useState(false);
  const [guides, setGuides] = useState([]);          // tours picked for this app
  const [guideList, setGuideList] = useState(false); // the pick-a-guide panel
  const [overlay, setOverlay] = useState(null);      // { page, label, menu? }
  const [overlayPage, setOverlayPage] = useState(null); // active page within the overlay
  const [tour, setTour] = useState(null);            // the running guide

  useEffect(() => {
    let alive = true;
    resolveApp(viewName)
      .then((r) => {
        if (!alive) return;
        setNeedsAuth(false);
        setFlowProjectId(r?.flow_project_id || null);
        setCompanions(companionsFrom(r));
        setGuideSlugs(Array.isArray(r?.guide_slugs) ? r.guide_slugs : null);
      })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.message || String(e);
        // 401 = no session at all. A share link must NOT bounce to the cortex
        // login (that is the whole point — the visitor may have no cortex
        // account, and cortex is not what they were sent to); show this app's
        // own gate instead. 403 stays "not shared with you": they are signed
        // in, they simply were not invited to this app.
        if (e?.status === 401 || e?.error === 'Unauthorized' || /unauthorized|401/i.test(msg)) {
          setNeedsAuth(true);
        } else if (e?.error === 'Forbidden' || /forbidden|not been shared|403/i.test(msg)) {
          setDenied(true);
        } else {
          setErr(msg);
        }
      });
    return () => { alive = false; };
  }, [viewName, authNonce]);

  // The gate's own form state. Kept local to the page: a share visitor has no
  // AuthContext (this route is mounted outside every provider on purpose).
  const submitGate = async (e) => {
    e?.preventDefault?.();
    if (gateBusy) return;
    setGateBusy(true);
    setGateErr(null);
    try {
      const r = await login({ email: gateEmail.trim(), password: gatePassword });
      if (!r?.access_token) throw new Error("Sign-in failed");
      // The ordinary cortex session key: one identity, not a second one to
      // keep in step. What the account may actually reach is decided
      // server-side — an app_user gets their apps and nothing else.
      localStorage.setItem("access_token", r.access_token);
      setGatePassword("");
      setAuthNonce((n) => n + 1); // re-run resolve with the new token
    } catch (e2) {
      setGateErr(e2?.message || "Invalid email or password.");
      setGateBusy(false);
      return;
    }
    setGateBusy(false);
  };

  // Is this companion on for this viewer? The ONLY question the dial asks, and
  // it is answered by the server's list — never by a scope test, never by a
  // data probe. (The old `full = scope === 'full'` gate lived here; it is what
  // made this surface disagree with every other one.)
  const on = (key) => companionOn(companions, key);

  // Stable iframe identity: the src carries only the INITIAL deep link — later
  // query changes (a guide stepping the app's ?page=, or the app reporting its
  // own route) go over the same postMessage bridge Apps.jsx uses, so the app
  // never remounts mid-session.
  const initialDeepLinkRef = useRef(deepLink);
  const embedSrc = useMemo(
    () => embedUrl(flowProjectId, viewName, initialDeepLinkRef.current),
    [flowProjectId, viewName]
  );
  const lastBridgedRef = useRef(initialDeepLinkRef.current);
  // Outer URL → iframe: forward query changes as the app's hash route.
  useEffect(() => {
    if (deepLink === lastBridgedRef.current) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    lastBridgedRef.current = deepLink;
    try { win.postMessage({ source: "cortex-app-route", hash: deepLink }, window.location.origin); } catch {}
  }, [deepLink]);
  // Iframe → outer URL: mirror the app's route into the share URL (replace, so
  // in-app paging doesn't pollute history).
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin && e.origin !== window.location.origin) return;
      const d = e.data;
      if (!d || d.source !== "cortex-view-route") return;
      const hash = String(d.hash || "").replace(/^#/, "");
      if (hash === lastBridgedRef.current) return;
      lastBridgedRef.current = hash;
      const qs = new URLSearchParams(hash).toString();
      navigate(
        `/p/${encodeURIComponent(projectSlug)}/b/${encodeURIComponent(branch || "main")}/share/apps/${encodeURIComponent(viewName)}${qs ? `?${qs}` : ""}`,
        { replace: true }
      );
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [projectSlug, branch, viewName, navigate]);

  // Guide CONTENT — the tours themselves, needed to RUN one. This is not an
  // availability probe: whether the Guides button exists is already settled by
  // the server's list. It loads only when Guides is on, and a failure leaves
  // the button in place (pressing it then opens an empty list rather than the
  // button silently never appearing — a visible nothing beats an invisible
  // decision). guide_slugs (the owner's per-guide picks, from the resolve) wins
  // over the default this-app-only filter (tour.app === view name).
  useEffect(() => {
    if (!flowProjectId || !on("guides")) { setGuides([]); return; }
    let alive = true;
    setGuides([]);
    loadProjectGuides(projectSlug)
      .then((tours) => {
        if (!alive) return;
        const picks = Array.isArray(guideSlugs) ? new Set(guideSlugs) : null;
        setGuides((tours || []).filter((t) =>
          picks ? picks.has(t?.__guide?.slug) : t?.__guide?.app === viewName
        ));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [flowProjectId, projectSlug, viewName, companions, guideSlugs]);

  // Loader gate: clear only once the view's React root mounts in the iframe
  // (same detection as Apps.jsx — a fixed-position [id$="-root"] / [data-view-root]).
  //
  // On FAILURE this must NOT keep spinning. An app that never mounts used to
  // leave the page on "Opening…" forever with no signal — that is what made a
  // refused studio socket (WS close 4403) so hard to diagnose. The timeout now
  // surfaces a real error with a retry.
  const [timedOut, setTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setViewReady(false);
    setTimedOut(false);
    if (!embedSrc) return;
    let done = false;
    const started = Date.now();
    const id = setInterval(() => {
      if (done) return;
      try {
        const doc = iframeRef.current?.contentDocument;
        if (doc) {
          const candidates = [
            doc.getElementById("customers-dashboard-root"),
            ...doc.querySelectorAll('[id$="-root"]:not(#root):not(#app-root):not(#flow-root)'),
            ...doc.querySelectorAll('[data-view-root]'),
          ].filter(Boolean);
          const mounted = candidates.some((el) => {
            if (el.hasAttribute("data-view-root")) return true;
            const cs = doc.defaultView?.getComputedStyle?.(el);
            return cs && cs.position === "fixed" && el.childElementCount > 0;
          });
          if (mounted) { done = true; clearInterval(id); setViewReady(true); }
        }
      } catch { /* same-origin; ignore transient nulls */ }
      if (Date.now() - started > 90000) { done = true; clearInterval(id); setTimedOut(true); }
    }, 400);
    return () => { done = true; clearInterval(id); };
  }, [embedSrc, attempt]);

  // The embedded studio reports an unauthorized/forbidden socket upgrade
  // (close code 4401/4403) to its host. Without this the page can only infer
  // failure from the timeout; with it, a permission problem is immediate and
  // named instead of looking like a slow load.
  useEffect(() => {
    const onMsg = (e) => {
      if (e?.data?.type !== "cortex:flow-ws-denied") return;
      if (e.data.code === 4403) setDenied(true);
      else setErr("Could not authenticate with the app service. Try signing in again.");
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const openOverlay = (page, label, menu) => {
    setDialOpen(false);
    setOverlayPage(page);
    setOverlay({ page, label, menu: menu || null });
  };

  const chatOn = on("chat");

  // The share link is LIVE CORTEX (unlike a deployment, which is a frozen
  // snapshot with no assistant behind it), so its chat is the CORTEX
  // ORCHESTRATOR — the same chat that delegates to the governance / engine /
  // analyst personas and to project agents — not the bare analyst chat it used
  // to be. Which of those personas it actually carries is the app owner's call:
  // the assistant resolves this app's Viewer options server-side and hands the
  // orchestrator exactly that set (assistant/companion-gate.js). So with
  // Governance ticked, "add a processing rule that flags negative quantities"
  // typed here authors the rule for real; with it unticked the governance
  // persona is ABSENT from the roster, not merely refused.
  const openChat = () => window.dispatchEvent(new CustomEvent("cortex:open-harness", { detail: { harness: "orchestrator" } }));

  // The dial's items, built by FILTERING the server's list. One expression, one
  // order, no per-item predicate — a companion is here iff the owner toggled it
  // on, which is the entire rule (shared/viewer-companions.js).
  //
  // Guides is the only item with an interior state: several tours need a picker
  // panel, one runs directly. That is presentation, not availability — the
  // button exists either way.
  const items = (companions || []).map((key) => {
    switch (key) {
      case "chat":
        // Cortex blue, matching the orchestrator's own window chrome
        // (HARNESS_CONFIG.orchestrator.titleBarColor) — it is the cortex chat
        // now, not the green analyst one.
        return { key, testid: "share-dial-chat", title: "Chat", Icon: MessageSquare, bg: "#3c5d88",
          onClick: () => { setDialOpen(false); openChat(); } };
      case "workflows":
        return { key, testid: "share-dial-workflows", title: "Workflows", Icon: Workflow, bg: "#0f172a",
          onClick: () => openOverlay("workflows", "Workflows") };
      case "guides":
        return guides.length === 1
          ? { key, testid: "share-dial-guide", title: guides[0].title || "Guide", label: "Guide", Icon: GraduationCap, bg: "#7c3aed",
              onClick: () => { setDialOpen(false); setTour(guides[0]); } }
          : { key, testid: "share-dial-guides", title: "Guides", Icon: GraduationCap, bg: "#7c3aed", picker: true,
              onClick: () => setGuideList((v) => !v) };
      case "pipeline":
        return { key, testid: "share-dial-pipeline", title: "Pipeline", Icon: GitBranch, bg: "#4f46e5",
          onClick: () => openOverlay("pipeline", "Pipeline") };
      case "governance":
        return { key, testid: "share-dial-governance", title: "Governance", Icon: ShieldCheck, bg: "#059669",
          onClick: () => openOverlay("data-dictionary", "Data Governance", dictionaryChildren) };
      case "docs":
        return { key, testid: "share-dial-docs", title: "Docs", Icon: BookText, bg: "#b45309",
          onClick: () => openOverlay("docs", "Docs") };
      default:
        return null;
    }
  }).filter(Boolean);

  const total = items.length;

  // With exactly ONE companion the dial degrades to a direct bubble — a fan of
  // one is two clicks for one action. Never for the multi-guide picker, which
  // needs the panel. Chat keeps its historical testid so the bubble and the
  // dial item read as the same control.
  const single = (total === 1 && !items[0].picker)
    ? { ...items[0], testid: items[0].key === "chat" ? "share-chat-button" : `share-single-${items[0].key}` }
    : null;

  // THE APP'S OWN GATE. Rendered before anything else can mount, so nothing
  // about the app — not its name, its data, its agents — reaches the page
  // until a session exists. There is deliberately no link to cortex here: the
  // visitor was sent to an app, and cortex is not their destination.
  if (needsAuth) {
    return (
      <div data-testid="share-gate" style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--page-bg, #eef2f8)", padding: 24 }}>
        <form
          onSubmit={submitGate}
          style={{ width: "100%", maxWidth: 380, background: "var(--card-bg, #fff)", borderRadius: 12, padding: 28, boxShadow: "0 10px 40px rgba(15,23,42,.12)", display: "flex", flexDirection: "column", gap: 14 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Lock size={20} style={{ color: "var(--page-muted, #64748b)" }} />
            <div style={{ fontSize: 17, fontWeight: 650, color: "var(--page-text, #0f172a)" }}>Sign in</div>
          </div>
          <div style={{ fontSize: 13.5, color: "var(--page-muted, #64748b)", lineHeight: 1.5 }}>
            This app was shared with you. Sign in to open it.
          </div>
          <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13, color: "var(--page-muted, #64748b)" }}>
            Email
            <input
              data-testid="share-gate-email"
              type="email"
              autoComplete="username"
              required
              value={gateEmail}
              onChange={(e) => setGateEmail(e.target.value)}
              style={{ height: 38, borderRadius: 7, border: "1px solid var(--page-divider, #d7dee8)", padding: "0 10px", fontSize: 14, color: "var(--page-text, #0f172a)", background: "transparent" }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13, color: "var(--page-muted, #64748b)" }}>
            Password
            <input
              data-testid="share-gate-password"
              type="password"
              autoComplete="current-password"
              required
              value={gatePassword}
              onChange={(e) => setGatePassword(e.target.value)}
              style={{ height: 38, borderRadius: 7, border: "1px solid var(--page-divider, #d7dee8)", padding: "0 10px", fontSize: 14, color: "var(--page-text, #0f172a)", background: "transparent" }}
            />
          </label>
          {gateErr && (
            <div data-testid="share-gate-error" style={{ fontSize: 13, color: "#b91c1c", lineHeight: 1.45 }}>{String(gateErr)}</div>
          )}
          <button
            data-testid="share-gate-submit"
            type="submit"
            disabled={gateBusy}
            style={{ height: 40, borderRadius: 7, border: 0, background: "var(--nav-active, #4f46e5)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: gateBusy ? "default" : "pointer", opacity: gateBusy ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            {gateBusy && <Loader2 className="animate-spin" size={15} />}
            {gateBusy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  if (denied) {
    return (
      <div data-testid="share-denied" style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, background: "var(--page-bg, #eef2f8)", color: "var(--page-muted, #64748b)", textAlign: "center", padding: 24 }}>
        <Lock size={32} />
        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--page-text, #0f172a)" }}>
          You don't have access to this app
        </div>
        <div style={{ fontSize: 14, maxWidth: 420 }}>
          Ask the app's owner to share it with you or make it available to the organization.
        </div>
      </div>
    );
  }

  // Small pill factory keeps the dial buttons uniform.
  const dialItem = (testid, label, Icon, bg, onClick, title) => (
    <button
      key={testid}
      type="button"
      data-testid={testid}
      title={title || label}
      onClick={onClick}
      style={{ background: bg, boxShadow: "0 4px 14px rgba(0,0,0,.28)" }}
      className="h-10 pl-3 pr-4 rounded-full flex items-center gap-2 text-white text-sm font-medium cursor-pointer border-0 hover:opacity-90 transition-all"
    >
      <Icon className="w-5 h-5" /> {label}
    </button>
  );

  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden", background: "var(--page-bg, #eef2f8)" }}>
      {embedSrc ? (
        <iframe
          ref={iframeRef}
          // Remount on retry: keying by `attempt` tears the old frame down and
          // re-runs the embed from scratch. Restarting only the poll would
          // watch a frame that already failed.
          key={attempt}
          data-testid="shared-app-iframe"
          src={embedSrc}
          title={viewName}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "0" }}
        />
      ) : null}
      {!viewReady && (
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, background: "var(--page-bg, #eef2f8)", color: "var(--page-muted, #64748b)", textAlign: "center", padding: 24 }}>
          {err ? (
            <div style={{ color: "#b91c1c", fontSize: 14 }}>{String(err)}</div>
          ) : timedOut ? (
            // Never leave the user on an endless spinner: say it failed and
            // offer a retry rather than pretending it is still loading.
            <>
              <AlertTriangle size={30} style={{ color: "#b45309" }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--page-text, #0f172a)" }}>
                This app didn't finish loading
              </div>
              <div style={{ fontSize: 14, maxWidth: 460 }}>
                It may still be starting up, or you may not have permission to run it.
                Try again, and contact the app's owner if it keeps failing.
              </div>
              <button
                onClick={() => setAttempt((n) => n + 1)}
                style={{ marginTop: 6, height: 36, padding: "0 16px", borderRadius: 5, border: 0, cursor: "pointer", background: "var(--primary, #3b82f6)", color: "#fff", fontSize: 14, fontWeight: 500 }}
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <Loader2 size={28} className="animate-spin" style={{ color: "var(--primary, #3b82f6)" }} />
              <div style={{ fontSize: 14 }}>Opening…</div>
            </>
          )}
        </div>
      )}
      {/* The chat engine — mounted only while the owner keeps chat ON, and
          its OWN launcher is always hidden here: the corner control is either
          the speed dial below or the single bubble, both opening this chat
          via cortex:open-harness. With chat off nothing can dispatch the event,
          so the engine is not mounted at all.
          ORCHESTRATOR, not analyst: this page is live cortex, so the chat is
          the full cortex chat, scoped to this app's Viewer options server-side
          (see openChat above). hideLauncher also keeps the orchestrator's own
          appOpen speed dial out of the way — this page draws its own. */}
      {flowProjectId && !denied && !err && chatOn && (
        <AssistantChat harness="orchestrator" hideLauncher />
      )}

      {/* Exactly ONE companion → render it as a direct bubble, styled like its
          dial item so the two states read as the same thing. Zero companions →
          the corner stays empty: the owner shared the bare app.
          NO FLICKER: the whole list arrives with the resolve, in one state
          update, so the corner goes straight from empty to its final shape
          instead of growing as async probes settle (which is what it used to
          do, and what made two surfaces disagree even mid-load). */}
      {flowProjectId && !denied && !err && !tour && total === 1 && single && (
        <button
          type="button"
          data-testid={single.testid}
          title={single.title}
          onClick={single.onClick}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.55"; }}
          style={{ width: 44, height: 44, background: single.bg, opacity: 0.55, boxShadow: "0 4px 14px rgba(0,0,0,.28)", transition: "opacity .2s" }}
          className="fixed bottom-6 right-6 z-[99999] rounded-full flex items-center justify-center cursor-pointer border-0 text-white"
        >
          <single.Icon className="w-5 h-5 text-white" />
        </button>
      )}

      {/* ── Speed dial — the ONE bottom-right button (same idiom, same LIST, as
             the in-cortex app view and the deployed portal). Items are the
             server's `companions`, rendered in order and in full: no data
             probe, no scope narrowing, nothing this page decides for itself.
             Only mounts when it has MULTIPLE companions to gather (or the one
             companion is the multi-guide picker, which needs this panel);
             hidden while a guide tour runs so the spotlight owns the corner. ── */}
      {flowProjectId && !denied && !err && !tour && (total > 1 || (total === 1 && !single)) && (
        <div className="fixed bottom-6 right-6 z-[99999] flex flex-col-reverse items-end gap-2">
          <button
            type="button"
            data-testid="share-speed-dial"
            title={dialOpen ? "Close" : "Companions"}
            onClick={() => { setDialOpen((v) => !v); setGuideList(false); }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
            onMouseLeave={(e) => { if (!dialOpen) e.currentTarget.style.opacity = "0.55"; }}
            style={{ width: 44, height: 44, background: "#3c5d88", opacity: dialOpen ? 1 : 0.55, boxShadow: "0 4px 14px rgba(0,0,0,.28)", transition: "opacity .2s" }}
            className="rounded-full flex items-center justify-center cursor-pointer border-0 text-white"
          >
            {dialOpen ? <X className="w-5 h-5 text-white" /> : <Plus className="w-5 h-5 text-white" />}
          </button>
          {dialOpen && items.map((it) => (
            it.picker ? (
              <div key={it.key} className="flex flex-col items-end gap-2">
                {guideList && (
                  <div
                    data-testid="share-guide-list"
                    className="w-72 max-h-80 overflow-y-auto rounded-xl bg-white shadow-2xl border border-black/10 py-1"
                  >
                    {guides.length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-500">No guides available.</div>
                    )}
                    {guides.map((g) => (
                      <button
                        key={g.key}
                        type="button"
                        data-testid={`share-guide-${g.__guide?.slug || g.key}`}
                        onClick={() => { setDialOpen(false); setGuideList(false); setTour(g); }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-800 bg-transparent border-0 cursor-pointer hover:bg-slate-100 flex items-center gap-2"
                      >
                        <GraduationCap className="w-4 h-4 shrink-0 text-violet-600" />
                        <span className="truncate">{g.title}</span>
                      </button>
                    ))}
                  </div>
                )}
                {dialItem(it.testid, it.label || it.title, it.Icon, it.bg, it.onClick, it.title)}
              </div>
            ) : dialItem(it.testid, it.label || it.title, it.Icon, it.bg, it.onClick, it.title)
          ))}
        </div>
      )}

      {/* Companion overlay — a cortex page, chromeless (?embed=1), same as the
          in-cortex EmbedPageModal. Governance carries the section menu (the
          same list Layout's modal uses) — without it there is no way to move
          between Elements, Sources, Rules etc. */}
      {overlay && (
        <div style={{ position: "fixed", inset: 24, zIndex: 100000, borderRadius: 12, overflow: "hidden", boxShadow: "0 24px 80px rgba(0,0,0,.5)", background: "#fff", border: "1px solid rgba(0,0,0,.12)", display: "flex" }}
          data-testid="share-overlay">
          <button
            type="button"
            data-testid="share-overlay-close"
            onClick={() => { setOverlay(null); setOverlayPage(null); }}
            title="Close"
            style={{ position: "absolute", top: 10, right: 10, zIndex: 2, width: 32, height: 32, borderRadius: "50%", border: 0, background: "rgba(15,23,42,.75)", color: "#fff", cursor: "pointer", fontSize: 14 }}
          >
            ✕
          </button>
          {overlay.menu && (
            <nav data-testid="share-overlay-menu" style={{ width: 200, flexShrink: 0, borderRight: "1px solid rgba(0,0,0,.08)", overflowY: "auto", padding: "8px 0", background: "#f8fafc" }}>
              {overlay.menu.map((it) => (
                <button
                  key={it.page}
                  type="button"
                  onClick={() => setOverlayPage(it.page)}
                  className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 border-0 cursor-pointer transition-colors"
                  style={{ background: (overlayPage || overlay.page) === it.page ? "#e2e8f0" : "transparent", color: "#0f172a" }}
                >
                  {it.icon ? <it.icon size={14} /> : null}
                  {it.title}
                </button>
              ))}
            </nav>
          )}
          <iframe
            key={overlayPage || overlay.page}
            title={overlay.label}
            data-testid="share-overlay-iframe"
            src={`/p/${encodeURIComponent(projectSlug)}/b/${encodeURIComponent(branch || "main")}/${overlayPage || overlay.page}?embed=1`}
            style={{ flex: 1, width: "100%", height: "100%", border: 0 }}
          />
        </div>
      )}

      {/* Guide tour engine — Layout owns it everywhere else, but the share
          page has no Layout, so it mounts its own instance. Steps that point
          at THIS app's in-cortex page (apps/<view>?page=…) are rewritten onto
          the share path (the ?page= query still drives the embed); steps that
          point at other cortex pages are NOT followed — the share surface has
          no such pages, so those steps degrade to centered cards in place. */}
      {tour && (
        <TutorialFlow
          tour={tour}
          navigate={(url) => {
            try {
              const s = String(url || "");
              const qi = s.indexOf("?");
              const path = qi === -1 ? s : s.slice(0, qi);
              const search = qi === -1 ? "" : s.slice(qi);
              const m = path.match(/^\/p\/[^/]+\/b\/[^/]+\/apps\/(.+)$/);
              if (m && decodeURIComponent(m[1]) === viewName) {
                navigate(`/p/${encodeURIComponent(projectSlug)}/b/${encodeURIComponent(branch || "main")}/share/apps/${m[1]}${search}`);
              }
            } catch { /* stay put */ }
          }}
          onClose={() => setTour(null)}
        />
      )}
    </div>
  );
}
